import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orgAPI, billingAPI, domainAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  Building,
  Palette,
  CreditCard as BillingIcon,
  Globe,
  Plus,
  Trash2,
  Copy,
  CheckCircle,
  Clock,
  Calendar,
  UserPlus,
  ShoppingBag,
  ClipboardList
} from 'lucide-react';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [billing, setBilling] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  
  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    brand_logo_url: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];
        
        if (user?.org_id) {
          promises.push(orgAPI.get(user.org_id));
        }
        
        if (user?.role === 'super_admin' || user?.role === 'company_admin') {
          promises.push(billingAPI.get());
          promises.push(domainAPI.list());
        } else {
          promises.push(Promise.resolve(null));
          promises.push(Promise.resolve({ data: [] }));
        }
        
        const results = await Promise.all(promises);
        
        if (user?.org_id && results[0]) {
          setOrganization(results[0].data);
          setBrandForm({
            brand_name: results[0].data.brand_name || '',
            brand_logo_url: results[0].data.brand_logo_url || '',
          });
        }
        
        if (results[1]?.data) {
          setBilling(results[1].data);
        }
        
        if (results[2]?.data) {
          setDomains(results[2].data);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSaveBranding = async () => {
    if (!user?.org_id) return;
    
    setSaving(true);
    try {
      await orgAPI.update(user.org_id, brandForm);
      toast.success('Branding updated!');
    } catch (error) {
      toast.error('Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain) {
      toast.error('Please enter a domain');
      return;
    }
    
    try {
      const response = await domainAPI.create({ hostname: newDomain });
      setDomains([...domains, response.data]);
      setNewDomain('');
      setShowDomainForm(false);
      toast.success('Domain added! Configure DNS as shown below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Delete this domain?')) return;
    
    try {
      await domainAPI.delete(domainId);
      setDomains(domains.filter(d => d.custom_domain_id !== domainId));
      toast.success('Domain deleted');
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings', active: true },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h1>
            <NotificationBell />
          </div>
        </header>

        <main className="p-6 max-w-4xl" data-testid="settings-page">
          {/* Account Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Account Information
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Name</Label>
                <p className="font-medium text-slate-900">{user?.name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Email</Label>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
              <div>
                <Label className="text-slate-500">Role</Label>
                <p className="font-medium text-slate-900 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <div>
                <Label className="text-slate-500">Status</Label>
                <p className="font-medium text-emerald-600 capitalize">{user?.status}</p>
              </div>
            </div>
          </div>

          {/* Organization Branding (for company_admin) */}
          {(user?.role === 'company_admin' || user?.role === 'super_admin') && organization && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Palette className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Organization Branding
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="brand_name">Brand Name</Label>
                  <Input
                    id="brand_name"
                    className="mt-1"
                    value={brandForm.brand_name}
                    onChange={(e) => setBrandForm({ ...brandForm, brand_name: e.target.value })}
                    placeholder="Your company name"
                    data-testid="brand-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="brand_logo">Brand Logo URL</Label>
                  <div className="flex gap-3 mt-1">
                    <Input
                      id="brand_logo"
                      value={brandForm.brand_logo_url}
                      onChange={(e) => setBrandForm({ ...brandForm, brand_logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      data-testid="brand-logo-input"
                    />
                    {brandForm.brand_logo_url && (
                      <img
                        src={brandForm.brand_logo_url}
                        alt="Logo preview"
                        className="h-10 w-auto object-contain"
                      />
                    )}
                  </div>
                </div>
                <Button
                  onClick={handleSaveBranding}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-branding-btn"
                >
                  {saving ? <div className="spinner w-4 h-4 border-2" /> : 'Save Branding'}
                </Button>
              </div>
            </div>
          )}

          {/* Custom Domains (for company_admin + super_admin) */}
          {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="custom-domains-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Custom Domains
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDomainForm(true)}
                  data-testid="add-domain-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Domain
                </Button>
              </div>

              {showDomainForm && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <Label>Domain Hostname</Label>
                  <div className="flex gap-3 mt-1">
                    <Input
                      placeholder="tap.yourcompany.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      data-testid="new-domain-input"
                    />
                    <Button onClick={handleAddDomain} className="bg-indigo-600 hover:bg-indigo-700">
                      Add
                    </Button>
                    <Button variant="ghost" onClick={() => setShowDomainForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {domains.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No custom domains configured</p>
                  <p className="text-sm text-slate-400">Add a custom domain to white-label your profiles</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {domains.map((domain) => (
                    <div key={domain.custom_domain_id} className="border border-slate-200 rounded-lg p-4" data-testid={`domain-${domain.custom_domain_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-slate-600" />
                          <span className="font-semibold text-slate-900">{domain.hostname}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            domain.status === 'verified' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {domain.status === 'verified' ? (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Verified
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Pending
                              </span>
                            )}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDomain(domain.custom_domain_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {domain.dns_instructions && (
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-slate-700 mb-2">DNS Configuration Required:</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Type:</span>
                              <code className="bg-slate-200 px-2 py-0.5 rounded">{domain.dns_instructions.type}</code>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Name:</span>
                              <div className="flex items-center gap-2">
                                <code className="bg-slate-200 px-2 py-0.5 rounded">{domain.dns_instructions.name}</code>
                                <button
                                  onClick={() => copyToClipboard(domain.dns_instructions.name)}
                                  className="text-indigo-600 hover:text-indigo-700"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Value:</span>
                              <div className="flex items-center gap-2">
                                <code className="bg-slate-200 px-2 py-0.5 rounded">{domain.dns_instructions.value}</code>
                                <button
                                  onClick={() => copyToClipboard(domain.dns_instructions.value)}
                                  className="text-indigo-600 hover:text-indigo-700"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-3">
                            Add this CNAME record to your DNS provider. Verification may take up to 24 hours.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Billing */}
          {billing && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <BillingIcon className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Billing (Mock)
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Plan</p>
                  <p className="text-xl font-bold text-slate-900 capitalize">{billing.plan}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Active Users</p>
                  <p className="text-xl font-bold text-slate-900">{billing.active_users}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Price per User</p>
                  <p className="text-xl font-bold text-slate-900">${billing.price_per_user}/mo</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-indigo-600">Monthly Cost</p>
                  <p className="text-xl font-bold text-indigo-600">${billing.monthly_cost}</p>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                * This is a mock billing system. Stripe integration can be added later.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
